import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ApiError } from "../../lib/api";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";

/**
 * Shown over the current page after the idle period. Password only (no code).
 * The page underneath is never unmounted, so the URL and its state are kept.
 */
export function AdminLockScreen() {
  const { locked, role, unlock, logout } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const open = locked && role === "admin";

  useEffect(() => {
    if (!open) {
      setPassword("");
      setError("");
    }
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError("");
    try {
      await unlock(password);
    } catch (err) {
      setPassword("");
      if (err instanceof ApiError && err.status === 429) {
        setError("Too many attempts. Wait a few minutes and try again.");
      } else if (err instanceof ApiError && err.status === 401 && err.message) {
        setError(err.message);
      } else {
        setError("Could not unlock. Check your connection and try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => undefined}
      title="Session locked"
      description="You were away for a while. Enter your password to continue where you left off."
      size="sm"
      closeOnBackdrop={false}
      closeOnEscape={false}
      showCloseButton={false}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-center gap-3 rounded-tile bg-accent-soft p-3 text-primary">
          <Lock size={18} aria-hidden="true" />
          <span className="text-sm font-semibold text-foreground">No verification code needed</span>
        </div>
        <div>
          <label htmlFor="lock-password" className="mb-1 block text-sm font-semibold text-foreground">
            Password
          </label>
          <input
            id="lock-password"
            type="password"
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-describedby={error ? "lock-error" : undefined}
            className="field w-full rounded-control px-3 py-2.5 text-base sm:text-sm"
          />
          {error && (
            <p id="lock-error" role="alert" className="mt-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <Button type="submit" className="w-full" loading={busy} loadingText="Unlocking…" disabled={!password}>
          Unlock
        </Button>
        <Button type="button" variant="ghost" className="w-full" onClick={() => logout(true)}>
          Sign out instead
        </Button>
      </form>
    </Modal>
  );
}
